import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

interface UnitOption {
  id: string;
  name: string;
  short_name: string;
  is_matrix: boolean;
  is_active: boolean;
}

interface UserUnitLink {
  id: string;
  unit_id: string;
  is_default: boolean;
  can_manage: boolean;
  unit: { id: string; name: string; short_name: string; is_matrix: boolean; is_active: boolean };
}

interface FormRow {
  unit_id: string;
  is_selected: boolean;
  is_default: boolean;
  can_manage: boolean;
}

interface Props {
  open: boolean;
  userId: string | null;
  userName?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

const UserUnitsDialog: React.FC<Props> = ({ open, userId, userName, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [rows, setRows] = useState<Record<string, FormRow>>({});

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [unitsRes, linksRes] = await Promise.all([
        axios.get(getApiUrl('/units'), { params: { include_inactive: false } }),
        axios.get(getApiUrl(`/units/users/${userId}/units`)),
      ]);
      const allUnits: UnitOption[] = unitsRes.data?.data || [];
      const links: UserUnitLink[] = linksRes.data?.data || [];

      setUnits(allUnits);

      const initial: Record<string, FormRow> = {};
      allUnits.forEach((u) => {
        const found = links.find((l) => l.unit_id === u.id || l.unit?.id === u.id);
        initial[u.id] = {
          unit_id: u.id,
          is_selected: !!found,
          is_default: !!found?.is_default,
          can_manage: !!found?.can_manage,
        };
      });
      // Garantir 1 default
      const hasDefault = Object.values(initial).some((r) => r.is_selected && r.is_default);
      if (!hasDefault) {
        const firstSelected = Object.values(initial).find((r) => r.is_selected);
        if (firstSelected) initial[firstSelected.unit_id].is_default = true;
      }
      setRows(initial);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) load();
  }, [open, userId, load]);

  const toggleSelected = (unitId: string, checked: boolean) => {
    setRows((prev) => {
      const next = { ...prev, [unitId]: { ...prev[unitId], is_selected: checked } };
      // Se desmarcou o default, escolhe outro automaticamente
      if (!checked && next[unitId]?.is_default) {
        next[unitId].is_default = false;
        const fallback = Object.values(next).find((r) => r.is_selected);
        if (fallback) next[fallback.unit_id].is_default = true;
      }
      // Se nada está selecionado, garante a unidade atual selecionada
      const anySelected = Object.values(next).some((r) => r.is_selected);
      if (!anySelected) {
        next[unitId].is_selected = true;
        next[unitId].is_default = true;
      }
      return next;
    });
  };

  const setDefault = (unitId: string) => {
    setRows((prev) => {
      const next: Record<string, FormRow> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k] = { ...v, is_default: k === unitId };
      });
      // Garantir que a unidade marcada como default esteja também selecionada
      if (next[unitId]) next[unitId].is_selected = true;
      return next;
    });
  };

  const toggleManage = (unitId: string, checked: boolean) => {
    setRows((prev) => ({ ...prev, [unitId]: { ...prev[unitId], can_manage: checked } }));
  };

  const save = async () => {
    if (!userId) return;
    const selected = Object.values(rows).filter((r) => r.is_selected);
    if (selected.length === 0) {
      alert('Selecione pelo menos uma unidade.');
      return;
    }
    if (selected.filter((r) => r.is_default).length !== 1) {
      alert('Marque exatamente uma unidade como padrão.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(getApiUrl(`/units/users/${userId}/units`), {
        units: selected.map((r) => ({ unit_id: r.unit_id, is_default: r.is_default, can_manage: r.can_manage })),
      });
      onSaved?.();
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erro ao salvar unidades do usuário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ color: '#0d9488' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Unidades do usuário</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {userName && (
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Vincule {userName} a uma ou mais unidades; defina a padrão e quem é gestor.
          </Typography>
        )}
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#14b8a6' }} />
          </Box>
        ) : units.length === 0 ? (
          <Typography sx={{ color: '#94a3b8' }}>Nenhuma unidade ativa cadastrada.</Typography>
        ) : (
          <RadioGroup value={Object.values(rows).find((r) => r.is_default)?.unit_id || ''}>
            {units.map((u) => {
              const r = rows[u.id];
              if (!r) return null;
              return (
                <Box
                  key={u.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: r.is_selected ? '#f0fdfa' : 'transparent',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                  }}
                >
                  <Checkbox
                    checked={!!r.is_selected}
                    onChange={(e) => toggleSelected(u.id, e.target.checked)}
                    size="small"
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{u.name}</Typography>
                      {u.is_matrix && (
                        <Chip
                          label="Matriz"
                          size="small"
                          icon={<StarIcon sx={{ fontSize: 12 }} />}
                          sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fef9c3', color: '#854d0e' }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{u.short_name}</Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ ml: 1 }}
                    control={
                      <Radio
                        size="small"
                        checked={!!r.is_default}
                        disabled={!r.is_selected}
                        onChange={() => setDefault(u.id)}
                      />
                    }
                    label={<Typography variant="caption">Padrão</Typography>}
                  />
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        color="success"
                        checked={!!r.can_manage}
                        disabled={!r.is_selected}
                        onChange={(e) => toggleManage(u.id, e.target.checked)}
                      />
                    }
                    label={<Typography variant="caption">Gestor</Typography>}
                  />
                </Box>
              );
            })}
          </RadioGroup>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Padrão: unidade carregada no login. Gestor: pode gerir configurações daquela unidade.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || loading} sx={{ bgcolor: '#14b8a6' }}>
          {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserUnitsDialog;
